import { ExternalLink, Info, MapPin } from "lucide-react";
import RichText from "@/components/RichText";
import { useT, useLoc } from "@/lib/i18n";

export const OTA = { booking: "Booking.com", agoda: "Agoda", traveloka: "Traveloka", tiket: "Tiket.com", google: "Google" };

export const OtaScores = ({ items = [] }) => {
  const t = useT();
  const loc = t("ulasan") === "reviews" ? "en-US" : "id-ID";
  items = items.filter((s) => Number(s.score) > 0);
  if (!items.length) return null;
  return (
    <div className="grid gap-1.5 border-t border-slate-100 pt-3" data-testid="ota-scores">
      {items.map((s, i) => {
        const body = (
          <>
            <span className="font-semibold text-slate-800">{OTA[s.source]}</span>
            <span className="ml-auto flex items-center gap-2 text-xs text-slate-500">
              {s.count > 0 && <span>{s.count.toLocaleString(loc)} {t("ulasan")}</span>}
              <b className="rounded bg-[#003580] px-1.5 py-0.5 text-white">{Number(s.score).toFixed(1)}</b>
              {s.url && <ExternalLink className="h-3.5 w-3.5" />}
            </span>
          </>
        );
        const cls = "flex items-center gap-2 rounded-md px-2 py-1.5 text-sm";
        return s.url
          ? <a key={i} href={s.url} target="_blank" rel="noopener noreferrer nofollow" data-testid={`ota-score-${s.source}`} className={`${cls} hover:bg-slate-50`}>{body}</a>
          : <div key={i} data-testid={`ota-score-${s.source}`} className={cls}>{body}</div>;
      })}
    </div>
  );
};

export const MapBox = ({ p }) => {
  const t = useT();
  const q = (p.map_query || [p.address, p.area, p.city].filter(Boolean).join(", ")).trim();
  if (!q) return null;
  const enc = encodeURIComponent(q);
  return (
    <div className="border-t border-slate-100 pt-3" data-testid="property-map">
      <div className="overflow-hidden rounded-lg border border-slate-200">
        <iframe title={t("Peta lokasi")} data-testid="property-map-frame" src={`https://www.google.com/maps?q=${enc}&z=15&output=embed`}
          className="block h-44 w-full" loading="lazy" referrerPolicy="no-referrer-when-downgrade" />
      </div>
      <a href={`https://www.google.com/maps/search/?api=1&query=${enc}`} target="_blank" rel="noopener noreferrer" data-testid="property-map-open"
        className="mt-1.5 inline-flex items-center gap-1 text-xs font-semibold text-[#006CE4] hover:underline"><ExternalLink className="h-3.5 w-3.5" />{t("Buka di Google Maps")}</a>
    </div>
  );
};

export const Attractions = ({ items = [] }) => {
  const t = useT();
  const loc = useLoc();
  if (!items.length) return null;
  return (
    <div className="border-t border-slate-100 pt-3" data-testid="attractions">
      <p className="font-heading text-sm font-bold text-slate-900">{t("Tempat menarik di sekitar")}</p>
      <ul className="mt-2 grid gap-2">
        {items.map((a, i) => (
          <li key={i} data-testid={`attraction-${i}`} className="flex gap-2 text-sm">
            <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-[#006CE4]" />
            <span className="min-w-0 flex-1"><span className="font-semibold text-slate-800">{loc(a, "name")}</span>
              {loc(a, "note") && <span className="block text-xs text-slate-500">{loc(a, "note")}</span>}</span>
            {a.distance && <span className="shrink-0 text-xs font-semibold text-slate-600">{a.distance}</span>}
          </li>
        ))}
      </ul>
    </div>
  );
};

export const InfoNote = ({ note }) => {
  const loc = useLoc();
  if (!note?.enabled || !loc(note, "body")) return null;
  return (
    <div className="rounded-xl border border-amber-200 bg-amber-50 p-4" data-testid="info-note">
      <p className="flex items-center gap-1.5 font-heading text-sm font-bold text-amber-900"><Info className="h-4 w-4" />{loc(note, "title") || "Info"}</p>
      <RichText text={loc(note, "body")} className="mt-2 text-sm text-amber-950" testid="info-note-body" />
    </div>
  );
};
